"use client";

import Image from "next/image";
import Link from "next/link";
import { Calendar, User, ArrowRight, Tag } from "lucide-react";
import { useState, useEffect } from "react";
import api from "@/lib/axiosInstance";

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  category: string;
  imageUrl: string;
  featured: boolean;
  readTime: string;
  views: number;
  createdAt: string;
  published: boolean;
}

function getImageUrl(url: string): string {
  if (!url) return "/assets/img/default-blog.jpg";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return url;
  return "/" + url;
}

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(["All"]);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await api.get("/blog");
      const posts = response.data.posts || [];
      
      setBlogPosts(posts);
      
      // Extract unique categories
      const categorySet = new Set<string>(posts.map((post: BlogPost) => post.category));
      const uniqueCategories: string[] = ["All", ...Array.from(categorySet)];
      setCategories(uniqueCategories);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      setLoading(false);
    }
  };

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FFB600] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div className="relative w-full h-[400px] flex items-center justify-center">
        <div className="absolute inset-0 bg-[#002301]"></div>
        
        <div className="relative z-10 text-center px-8">
          <h1 className="text-white text-[42px] sm:text-[56px] leading-[100%] tracking-[-0.5%] font-light">
            Solar <span className="text-[#FFB600]">News & Insights</span>
          </h1>
          <p className="text-white text-[16px] mt-4 max-w-2xl mx-auto">
            Stay updated with the latest trends, guides, and insights from Nigeria's solar energy industry
          </p>
        </div>
      </div>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <div className="max-w-7xl mx-auto px-8 py-16">
          <h2 className="text-[#002301] dark:text-white text-[32px] font-medium mb-8">Featured Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredPosts.map((post) => (
              <Link key={post.id} href={`/blog/${post.slug}`}>
                <div className="group cursor-pointer">
                  <div className="relative h-[300px] rounded-lg overflow-hidden mb-4">
                    <Image
                      src={getImageUrl(post.imageUrl)}
                      alt={post.title}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-[#FFB600] text-[#002301] px-3 py-1 rounded-full text-sm font-medium">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400 text-sm mb-2">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.createdAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {post.author}
                    </span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="text-[#002301] dark:text-white text-[24px] font-medium mb-2 group-hover:text-[#FFB600] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">{post.excerpt}</p>
                  <span className="text-[#FFB600] font-medium flex items-center gap-2 group-hover:gap-4 transition-all">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="bg-[#F4F6F6] dark:bg-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-[#FFB600] text-[#002301]"
                    : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-[#002301] hover:text-white"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* All Posts */}
      <div className="max-w-7xl mx-auto px-8 py-16">
        <h2 className="text-[#002301] dark:text-white text-[32px] font-medium mb-8">
          {selectedCategory === "All" ? "All Articles" : selectedCategory}
        </h2>
        {filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-600 dark:text-gray-400 text-lg">No articles found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link key={post.id} href={`/blog/${post.slug}`}>
                <div className="group cursor-pointer bg-white dark:bg-slate-800 rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                  <div className="relative h-[200px]">
                    <Image
                      src={getImageUrl(post.imageUrl)}
                      alt={post.title}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-4 h-4 text-[#FFB600]" />
                      <span className="text-[#FFB600] text-sm font-medium">{post.category}</span>
                    </div>
                    <h3 className="text-[#002301] dark:text-white text-[18px] font-medium mb-2 group-hover:text-[#FFB600] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center gap-2 text-gray-500 text-xs flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {post.author}
                      </span>
                      <span className="text-gray-300">·</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.createdAt)}
                      </span>
                      <span className="text-gray-300">·</span>
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
